// Analytics utility for tracking user interactions and conversions
export interface AnalyticsEvent {
  event: string;
  properties?: Record<string, any>;
  timestamp?: number;
  userId?: string;
}

export interface ConversionData {
  conversionType: string;
  value?: number;
  currency?: string;
  properties?: Record<string, any>;
}

// Analytics service interface
interface AnalyticsService {
  trackEvent: (event: string, properties?: Record<string, any>) => void;
  trackConversion: (conversionType: string, data?: ConversionData) => void;
  trackPageView: (page: string, properties?: Record<string, any>) => void;
  identifyUser: (userId: string, traits?: Record<string, any>) => void;
}

// Google Analytics 4 implementation
class GoogleAnalyticsService implements AnalyticsService {
  private gtag: any;

  constructor() {
    this.gtag = (window as any).gtag;
  }

  trackEvent(event: string, properties?: Record<string, any>) {
    if (this.gtag) {
      this.gtag("event", event, properties);
    }
  }

  trackConversion(conversionType: string, data?: ConversionData) {
    const eventData = {
      event_category: "conversion",
      event_label: conversionType,
      value: data?.value,
      currency: data?.currency,
      ...data?.properties,
    };

    this.trackEvent("conversion", eventData);
  }

  trackPageView(page: string, properties?: Record<string, any>) {
    this.trackEvent("page_view", { page, ...properties });
  }

  identifyUser(userId: string, traits?: Record<string, any>) {
    if (this.gtag) {
      this.gtag("config", "GA_MEASUREMENT_ID", {
        user_id: userId,
        custom_map: traits,
      });
    }
  }
}

// Mixpanel implementation (alternative analytics service)
class MixpanelService implements AnalyticsService {
  private mixpanel: any;

  constructor() {
    this.mixpanel = (window as any).mixpanel;
  }

  trackEvent(event: string, properties?: Record<string, any>) {
    if (this.mixpanel) {
      this.mixpanel.track(event, properties);
    }
  }

  trackConversion(conversionType: string, data?: ConversionData) {
    this.trackEvent("conversion", {
      conversion_type: conversionType,
      ...data,
    });
  }

  trackPageView(page: string, properties?: Record<string, any>) {
    this.trackEvent("page_view", { page, ...properties });
  }

  identifyUser(userId: string, traits?: Record<string, any>) {
    if (this.mixpanel) {
      this.mixpanel.identify(userId);
      if (traits) {
        this.mixpanel.people.set(traits);
      }
    }
  }
}

// Analytics manager that can use multiple services
class AnalyticsManager {
  private services: AnalyticsService[] = [];

  constructor() {
    // Initialize with available services
    if ((window as any).gtag) {
      this.services.push(new GoogleAnalyticsService());
    }

    if ((window as any).mixpanel) {
      this.services.push(new MixpanelService());
    }
  }

  trackEvent(event: string, properties?: Record<string, any>) {
    this.services.forEach((service) => {
      try {
        service.trackEvent(event, properties);
      } catch (error) {
        // console.error("Analytics service error:", error);
      }
    });
  }

  trackConversion(conversionType: string, data?: ConversionData) {
    this.services.forEach((service) => {
      try {
        service.trackConversion(conversionType, data);
      } catch (error) {
        // console.error("Analytics service error:", error);
      }
    });
  }

  trackPageView(page: string, properties?: Record<string, any>) {
    this.services.forEach((service) => {
      try {
        service.trackPageView(page, properties);
      } catch (error) {
        // console.error("Analytics service error:", error);
      }
    });
  }

  identifyUser(userId: string, traits?: Record<string, any>) {
    this.services.forEach((service) => {
      try {
        service.identifyUser(userId, traits);
      } catch (error) {
        // console.error("Analytics service error:", error);
      }
    });
  }
}

// Export singleton instance
export const analytics = new AnalyticsManager();

// Convenience functions
export const trackEvent = (event: string, properties?: Record<string, any>) => {
  analytics.trackEvent(event, properties);
};

export const trackConversion = (
  conversionType: string,
  data?: ConversionData
) => {
  analytics.trackConversion(conversionType, data);
};

export const trackPageView = (
  page: string,
  properties?: Record<string, any>
) => {
  analytics.trackPageView(page, properties);
};

export const identifyUser = (userId: string, traits?: Record<string, any>) => {
  analytics.identifyUser(userId, traits);
};

// Common event tracking functions
export const trackButtonClick = (
  buttonName: string,
  properties?: Record<string, any>
) => {
  trackEvent("button_click", { button_name: buttonName, ...properties });
};

export const trackFormSubmission = (
  formName: string,
  properties?: Record<string, any>
) => {
  trackEvent("form_submission", { form_name: formName, ...properties });
};

export const trackModalOpen = (
  modalName: string,
  properties?: Record<string, any>
) => {
  trackEvent("modal_open", { modal_name: modalName, ...properties });
};

export const trackJourneyStep = (
  step: number,
  stepName: string,
  properties?: Record<string, any>
) => {
  trackEvent("journey_step", { step, step_name: stepName, ...properties });
};

// Initialize analytics
export const initAnalytics = () => {
  if (typeof window === "undefined") return;

  // Initialize Google Analytics 4
  if (process.env.REACT_APP_GA_MEASUREMENT_ID) {
    // GA4 initialization would go here
    // For now, we'll just track that analytics is initialized
  }

  // Initialize Mixpanel
  if (process.env.REACT_APP_MIXPANEL_TOKEN) {
    // Mixpanel initialization would go here
  }
};
